import React, { useState, useCallback, useContext, createContext } from 'react';
import Toast from './Toast';

// トーストコンテキストの作成
const ToastContext = createContext();

/**
 * トーストコンテキストを使用するカスタムフック
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * トーストプロバイダーコンポーネント
 */
export const ToastProvider = ({ 
  children, 
  position = 'top-right', 
  maxToasts = 3,
  customStyles = {},
  showIcon = false
}) => {
  const [toasts, setToasts] = useState([]);

  /**
   * トーストを表示する関数
   * @param {string} message - 表示するメッセージ
   * @param {string} type - トーストの種類
   * @param {number} duration - 表示時間
   * @returns {string} トーストID
   */
  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    };

    setToasts(prevToasts => {
      // 最大数を超える場合は古いトーストを削除
      const updatedToasts = [...prevToasts, newToast];
      if (updatedToasts.length > maxToasts) {
        return updatedToasts.slice(-maxToasts);
      }
      return updatedToasts;
    });

    return id;
  }, [maxToasts]);

  /**
   * 特定のトーストを削除する関数
   * @param {string} id - トーストID
   */
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  /**
   * すべてのトーストを削除する関数
   */
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * 便利なメソッド群
   */
  const showSuccess = useCallback((message, duration) => {
    return showToast(message, 'success', duration);
  }, [showToast]);

  const showError = useCallback((message, duration) => {
    return showToast(message, 'error', duration);
  }, [showToast]);

  const showWarning = useCallback((message, duration) => {
    return showToast(message, 'warning', duration);
  }, [showToast]);

  const showInfo = useCallback((message, duration) => {
    return showToast(message, 'info', duration);
  }, [showToast]);

  const contextValue = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts,
    toasts
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* トーストの表示 */}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          isVisible={true}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          position={position}
          showIcon={showIcon}
          onClose={() => removeToast(toast.id)}
          customStyles={{
            // 複数のトーストがある場合の位置調整
            [position.includes('top') ? 'top' : 'bottom']: 
              `${(customStyles.top ? parseInt(customStyles.top) : 20) + (index * 80)}px`,
            ...customStyles
          }}
        />
      ))}
    </ToastContext.Provider>
  );
};

/**
 * 単一トーストコンポーネント（スタンドアローン使用）
 */
export const ToastManager = ({ children }) => {
  return (
    <ToastProvider>
      {children}
    </ToastProvider>
  );
};

export default ToastManager;